import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); }
  50% { box-shadow: 0 0 30px rgba(102, 126, 234, 0.6); }
`;

const NavContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.1)'};
  backdrop-filter: blur(20px);
  padding: 1rem 2rem;
  box-shadow: ${props => props.scrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 2px 10px rgba(0, 0, 0, 0.05)'};
  z-index: 1000;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(motion.div)`
  font-size: 1.5rem;
  font-weight: 900;
  color: ${props => props.scrolled ? '#2d3436' : '#ffffff'};
  text-shadow: ${props => props.scrolled ? 'none' : '1px 1px 2px rgba(0, 0, 0, 0.3)'};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    animation: ${glow} 2s ease-in-out infinite;
  }
`;

const NavList = styled.ul`
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  list-style: none;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: ${props => props.scrolled ? '#2d3436' : '#ffffff'};
  font-weight: 600;
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  transition: all 0.3s ease;
  position: relative;
  display: inline-block;
  text-shadow: ${props => props.scrolled ? 'none' : '1px 1px 2px rgba(0, 0, 0, 0.3)'};
  
  &::before {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 0;
    height: 2px;
    background: linear-gradient(45deg, #667eea, #764ba2);
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${props => props.scrolled ? '#667eea' : '#f093fb'};
    transform: translateY(-2px);
    
    &::before {
      width: 80%;
    }
  }
  
  &.active {
    color: ${props => props.scrolled ? '#667eea' : '#f093fb'};
    background: ${props => props.scrolled ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
    
    &::before {
      width: 80%;
    }
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  color: ${props => props.scrolled ? '#2d3436' : '#ffffff'};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 5px;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    display: block;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MobileMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 20px 20px;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileNavList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MobileNavLink = styled(Link)`
  text-decoration: none;
  color: #2d3436;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: block;
  text-align: center;
  
  &:hover {
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
    transform: translateX(10px);
  }
  
  &.active {
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: white;
  }
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/bonus-content', label: 'Bonus Content' },
    { path: '/contact', label: 'Contact' }
  ];
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  return (
    <NavContainer
      scrolled={scrolled}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <NavContent>
        <Logo
          scrolled={scrolled}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Winter North
        </Logo>
        
        <NavList>
          {navItems.map((item, index) => (
            <NavItem key={item.path}>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
              >
                <NavLink
                  to={item.path}
                  scrolled={scrolled}
                  className={isActive(item.path) ? 'active' : ''}
                >
                  {item.label}
                </NavLink>
              </motion.div>
            </NavItem>
          ))}
        </NavList>
        
        <MobileMenuButton
          scrolled={scrolled}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          whileTap={{ scale: 0.95 }}
        >
          {mobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
      </NavContent>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MobileNavList>
              {navItems.map((item, index) => (
                <motion.li
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <MobileNavLink
                    to={item.path}
                    className={isActive(item.path) ? 'active' : ''}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </MobileNavLink>
                </motion.li>
              ))}
            </MobileNavList>
          </MobileMenu>
        )}
      </AnimatePresence>
    </NavContainer>
  );
};

export default Navigation;
